import React from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'prototypes/securedCard/app.module.scss';
import AppChrome from 'prototypes/securedCard/components/AppChrome';
import CardFace from 'prototypes/securedCard/components/CardFace';
import StepFooter from 'prototypes/securedCard/components/StepFooter';
import { formatMoney } from 'prototypes/securedCard/formatMoney';
import { PrototypePath } from 'prototypes/securedCard/paths';
import { SAMPLE_PURCHASE } from 'prototypes/securedCard/prototypeAssumptions';
import { milestoneRank } from 'prototypes/securedCard/storyMath';
import { useStory } from 'prototypes/securedCard/StoryContext';
import { StoryMilestone } from 'prototypes/securedCard/types';

const SpendStep = () => {
  const navigate = useNavigate();
  const { state, projection, confirmPayment } = useStory();
  const paid = milestoneRank(state.milestone) >= milestoneRank(StoryMilestone.PurchasePaid);
  const purchaseLabel = formatMoney(projection.purchaseCents);

  return (
    <AppChrome
      step={PrototypePath.Spend}
      title="Spend it. It pays itself."
      subtitle="The purchase is paid from the savings that are already here. There is no bill to remember, and nothing left to revolve."
    >
      <div className={styles.stack}>
        <CardFace />
        <div className={styles.ledgerRow}>
          <div>
            <p className={styles.ledgerTitle}>{SAMPLE_PURCHASE.merchant}</p>
            <p className={styles.ledgerDetail}>{SAMPLE_PURCHASE.detail}</p>
            {projection.purchaseWasReduced && (
              <p className={styles.ledgerDetail}>
                The sample trip is {formatMoney(projection.requestedPurchaseCents)}. This line covers {purchaseLabel},
                so that's what gets paid.
              </p>
            )}
          </div>
          <p className={styles.amountOut}>{purchaseLabel}</p>
        </div>
        <div className={styles.figureList}>
          <div className={`${styles.figure} ${styles.figurePrimary}`}>
            <div>
              <p className={styles.figureLabel}>{paid ? 'Available after the payment' : 'Available before you pay'}</p>
              <p className={styles.figureHint}>The card stops at this amount</p>
            </div>
            <p className={styles.figureValue}>
              {formatMoney(paid ? projection.availableAfterPaymentCents : projection.availableCents)}
            </p>
          </div>
          <div className={styles.figure}>
            <div>
              <p className={styles.figureLabel}>Savings after the payment</p>
              <p className={styles.figureHint}>The purchase leaves the account. The rest stays.</p>
            </div>
            <p className={styles.figureValue}>
              {formatMoney(paid ? projection.savingsAfterPaymentCents : projection.savingsCents)}
            </p>
          </div>
        </div>
        {paid ? (
          <div className={`${styles.callout} ${styles.calloutGreen}`}>
            <p className={styles.calloutTitle}>Paid on time from your savings</p>
            <p className={styles.calloutBody}>
              {purchaseLabel} left the account on {projection.paymentDateLabel}. That on-time payment is what this
              design would report. You did not borrow the difference.
            </p>
          </div>
        ) : (
          <p className={styles.notice}>
            Spending stays inside the savings. The card does not lend past the available line.
          </p>
        )}
      </div>
      <StepFooter
        backTo={PrototypePath.Growth}
        nextLabel={paid ? 'See what you keep' : `Pay ${purchaseLabel} from savings`}
        onNext={() => {
          if (!paid) {
            confirmPayment();
            return;
          }
          navigate(PrototypePath.Keep);
        }}
      />
    </AppChrome>
  );
};

export default SpendStep;
